import { Card, Grid, Stack, Text, Title } from "@mantine/core";
import {
  IChecklistItemPopulated,
  IChecklistItemRow,
  onChecklistRowChange,
} from "@mctypes/checklist.types";
import { Paths, PathValue } from "@mctypes/global.types";
import React, { useCallback } from "react";
import { useLocation, useParams } from "react-router-dom";
import { McTitle } from "../../../../components";
import FoldableSection from "../../../../components/FoldableSection/FoldableSection";
import { LinkifiedText } from "../../../../components/LinkifiedText";
import McInputNew from "../../../../components/McInput/McInputNew";
import McTextAreaNew from "../../../../components/McInput/McTextAreaNew";
import { useAppSelector } from "../../../../hooks";
import {
  usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation,
  usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation,
} from "../../../../../openapi";
import "../checklistBoardApi.enhance";
import { selectChecklistModalViewModel } from "../checklistSelectors";
import ChecklistDetailsRowChecksSectionRefactored from "./ChecklistDetailsRowChecksSectionRefactored";

const ChecklistDetailsRefactored: React.FC = () => {
  const { checklistId } = useParams<{ checklistId: string }>();
  const location = useLocation();
  const checklistRowId = new URLSearchParams(location.search).get("id");
  const { checklist, checklistRow, editable, isItem, isTemplate, projectId, referenceValue } = useAppSelector((state) =>
    selectChecklistModalViewModel(state, checklistId, checklistRowId),
  );
  const [updateCategory] = usePutProjectByProjectIdChecklistAndChecklistIdCategoryCategoryIdMutation();
  const [updateItem] = usePutProjectByProjectIdChecklistAndChecklistIdItemItemIdMutation();

  const updateItemAttribute = useCallback(
    <P extends Paths<IChecklistItemPopulated, 4>>(
      rowId: string,
      path: P,
      oldValue: string,
      newValue: PathValue<IChecklistItemPopulated, P>,
    ) =>
      void updateItem({
        projectId,
        checklistId: checklist?._id ?? "",
        itemId: rowId,
        body: {
          path,
          oldValue,
          newValue,
        },
      }),
    [checklist?._id, projectId, updateItem],
  );

  const updateCategoryAttribute = useCallback(
    <T extends keyof IChecklistItemRow>(attribute: string, newValue: IChecklistItemRow[T], oldValue: string) =>
      void updateCategory({
        projectId,
        checklistId: checklist?._id ?? "",
        categoryId: checklistRow?._id ?? "",
        body: {
          attribute,
          oldValue,
          newValue,
        },
      }),
    [checklist?._id, checklistRow?._id, projectId, updateCategory],
  );

  const updateRowParameter = useCallback<onChecklistRowChange>((oldValue, newValue, parameter) => {
    if (checklistRow == null) {
      return;
    }

    if (oldValue === newValue) {
      return;
    }

    if (isItem === true) {
      updateItemAttribute(
        checklistRow._id,
        parameter as Paths<IChecklistItemPopulated, 4>,
        oldValue?.toString() ?? "",
        newValue as PathValue<IChecklistItemPopulated, Paths<IChecklistItemPopulated, 4>>,
      );
      return;
    }

    updateCategoryAttribute(parameter, newValue, oldValue?.toString() ?? "");
  }, [checklistRow?._id, isItem, updateCategoryAttribute, updateItemAttribute]);

  if (checklist == null || checklistRow == null) {
    return null;
  }

  return (
    <Stack gap="sm">
      <FoldableSection storageKey="checklist-modal-details">
        <FoldableSection.Header>
          <Title order={6}>Detaljer</Title>
        </FoldableSection.Header>

        <FoldableSection.Body>
          <Card w="100%" bg="#f8f9fa" bd="1px solid var(--mantine-color-warmgray-2)">
            <Stack gap="sm">
              <Grid>
                <Grid.Col span={12}>
                  <McTitle title="Tittel">
                    {editable === true ? (
                      <Stack gap={6}>
                        {isItem === true ? (
                          <McTextAreaNew
                            required={true}
                            autosize
                            onBlur={(event) => updateRowParameter(checklistRow.title, event.target.value, "title")}
                            value={checklistRow.title}
                          />
                        ) : (
                          <McInputNew
                            required={true}
                            onBlur={(event) => updateRowParameter(checklistRow.title, event.target.value, "title")}
                            value={checklistRow.title}
                          />
                        )}

                        {isTemplate !== true && (
                          <McInputNew
                            required={false}
                            placeholder="Referanse"
                            onBlur={(event) =>
                              updateRowParameter(checklistRow.reference, event.target.value, "reference")
                            }
                            value={checklistRow.reference ?? ""}
                          />
                        )}
                      </Stack>
                    ) : (
                      <Stack gap={2}>
                        <Text size="sm">
                          <LinkifiedText text={checklistRow.title} />
                        </Text>
                        {isTemplate !== true && referenceValue.length > 0 && (
                          <Text size="xs" c="dimmed">
                            <LinkifiedText text={referenceValue} />
                          </Text>
                        )}
                      </Stack>
                    )}
                  </McTitle>
                </Grid.Col>
              </Grid>

              <ChecklistDetailsRowChecksSectionRefactored />
            </Stack>
          </Card>
        </FoldableSection.Body>
      </FoldableSection>
    </Stack>
  );
};

export default ChecklistDetailsRefactored;
